/**
 * S3 upload utility for RTE photo uploads
 * 
 * Provides complete upload pipeline: validate → resize → presign → upload
 */

import { validateImageFile, resizeImage } from './imageResize';
import { apiBaseUrl } from './api';

// PUT a blob to S3 via XHR so we can report real upload progress to the
// UI. fetch() exposes no upload-progress event. Returns a promise and
// exposes an abort() method on the promise for cancellation.
const putWithProgress = (uploadUrl, blob, onProgress) => {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    if (typeof onProgress === 'function') {
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', 'image/jpeg');
    xhr.send(blob);
  });
  promise.abort = () => xhr.abort();
  return promise;
};

/**
 * Complete photo upload pipeline to S3
 * @param {Object} params - Upload parameters
 * @param {File} params.file - The image file to upload
 * @param {string} params.transactionId - Transaction ID for S3 key prefixing
 * @param {string} params.category - Photo category (e.g., 'fullPackage', 'plates')
 * @param {Object} [params.sdk] - Sharetribe SDK instance (unused today)
 * @param {(pct: number) => void} [params.onProgress] - Upload progress callback (0-100)
 * @param {AbortSignal} [params.signal] - Optional AbortSignal to cancel mid-upload
 * @returns {Promise<Object>} - { key: string, viewUrl: string, uploadedAt: string }
 */
export const uploadToS3 = async ({ file, transactionId, category, sdk, onProgress, signal }) => {
  let inFlightPut = null;
  const onAbort = () => {
    if (inFlightPut && typeof inFlightPut.abort === 'function') inFlightPut.abort();
  };
  if (signal) {
    if (signal.aborted) throw new Error('Upload cancelled');
    signal.addEventListener('abort', onAbort);
  }

  try {
    // Step 1: Validate the image file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Step 2: Resize the image
    const resizedBlob = await resizeImage(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.8,
      type: 'image/jpeg'
    });

    // Step 3: Get presigned URLs from our API. Routes through apiBaseUrl()
    // so the dev frontend (port 3000) can reach the dev backend (port 3500)
    // — a bare "/api/s3-presign" would hit the CRA dev server and 404.
    // Note: the server pins the S3 prefix server-side using the
    // authenticated user id + context enum. We no longer send `prefix`
    // (the server ignored it would be a vuln — see H-1). The server
    // builds `uploads/handoff/<userId>/<transactionId>/<subcategory>`.
    const presignResponse = await fetch(`${apiBaseUrl()}/api/s3-presign`, {
      method: 'POST',
      credentials: 'include', // send the Sharetribe session cookie
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: file.name,
        contentType: 'image/jpeg',
        context: 'handoff',
        transactionId,
        subcategory: category || 'general',
      }),
    });

    if (!presignResponse.ok) {
      const errorData = await presignResponse.json();
      throw new Error(errorData.error || 'Failed to get upload URL');
    }

    const { uploadUrl, viewUrl, key } = await presignResponse.json();

    // Step 4: Upload the resized image to S3 with real progress reporting.
    inFlightPut = putWithProgress(uploadUrl, resizedBlob, onProgress);
    await inFlightPut;

    // Step 5: Return the result
    if (signal) signal.removeEventListener('abort', onAbort);
    return {
      key,
      viewUrl,
      uploadedAt: new Date().toISOString(),
    };

  } catch (error) {
    if (signal) signal.removeEventListener('abort', onAbort);
    // Provide descriptive error messages for different failure scenarios
    if (error.message.includes('Failed to load image')) {
      throw new Error('Invalid image file. Please select a valid JPEG or PNG image.');
    }
    
    if (error.message.includes('Image processing failed')) {
      throw new Error('Unable to process the image. Please try a different file.');
    }
    
    if (error.message.includes('Rate limit exceeded')) {
      throw new Error('Too many uploads. Please wait a moment and try again.');
    }
    
    if (error.message.includes('Failed to get upload URL')) {
      throw new Error('Upload service temporarily unavailable. Please try again.');
    }
    
    if (error.message.includes('Upload failed with status')) {
      throw new Error('Upload failed. Please check your connection and try again.');
    }

    if (error.message.includes('Upload cancelled')) {
      // Propagate the cancel verbatim — the caller typically maps this to
      // a silent "user removed the in-flight photo" state, not a visible error.
      throw error;
    }

    if (error.message.includes('Network error') || error.message.includes('timed out')) {
      throw new Error("Couldn't upload. Check your connection and try again.");
    }

    // Re-throw the error with context
    throw new Error(`Upload failed: ${error.message}`);
  }
};

/**
 * Get a fresh presigned view URL for an existing S3 object.
 * View URLs from upload expire after 24 hours — use this to refresh them.
 * @param {string} key - The S3 object key
 * @returns {Promise<string>} - The presigned view URL
 */
export const getPresignedViewUrl = async key => {
  const response = await fetch(`${apiBaseUrl()}/api/s3-view-url`, {
    method: 'POST',
    credentials: 'include', // send the Sharetribe session cookie (H-1)
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });

  if (!response.ok) {
    throw new Error('Failed to get view URL. Please try again.');
  }

  const { viewUrl } = await response.json();
  return viewUrl;
};